import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

const Delta = ({ now, prev }) => {
  if (prev == null || now == null) return null;
  const diff = Math.round((now - prev) * 10) / 10;
  const Icon = diff > 0 ? TrendingUp : diff < 0 ? TrendingDown : Minus;
  const color = diff > 0 ? "text-emerald-600" : diff < 0 ? "text-red-500" : "text-slate-400";
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-medium ${color}`}>
      <Icon size={11} /> {diff > 0 ? `+${diff}` : diff} vs last week
    </span>
  );
};

const WeekStat = ({ label, value, prev, suffix = "", testId }) => (
  <div className="border border-slate-200 rounded-xl px-4 py-3" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
    <div className="text-2xl font-bold text-slate-900 mt-0.5">{value ?? "—"}{value != null ? suffix : ""}</div>
    <Delta now={value} prev={prev} />
  </div>
);

export const EngagementSummary = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/engagement?weeks=8").then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);

  if (!data) return null;
  const weeks = data.weeks || [];
  const cur = weeks[weeks.length - 1] || {};
  const prev = weeks[weeks.length - 2] || {};
  const chart = weeks.map((w) => ({
    ...w,
    label: new Date(w.week_start).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
  }));

  return (
    <div className="mb-10" data-testid="engagement-summary">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-lg font-semibold text-slate-900">Weekly engagement</h2>
        <span className="text-[11px] text-slate-400">Last 8 weeks · Mon–Sun (UTC)</span>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <WeekStat label="Sign-ups this week" value={cur.signups} prev={prev.signups} testId="week-signups" />
        <WeekStat label="Active learners" value={cur.active_learners} prev={prev.active_learners} testId="week-active" />
        <WeekStat label="Module completions" value={cur.completions} prev={prev.completions} testId="week-completions" />
        <WeekStat label="Avg quiz score" value={cur.avg_percentage} prev={prev.avg_percentage} suffix="%" testId="week-avg-score" />
      </div>
      <div className="border border-slate-200 rounded-xl p-4 bg-white" data-testid="engagement-chart">
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={chart} margin={{ top: 4, right: 8, left: -22, bottom: 0 }}>
            <defs>
              <linearGradient id="gSign" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#E53935" stopOpacity={0.25} />
                <stop offset="100%" stopColor="#E53935" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gComp" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#222222" stopOpacity={0.18} />
                <stop offset="100%" stopColor="#222222" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }} />
            <Area type="monotone" dataKey="signups" name="Sign-ups" stroke="#E53935" strokeWidth={2} fill="url(#gSign)" />
            <Area type="monotone" dataKey="completions" name="Completions" stroke="#222222" strokeWidth={2} fill="url(#gComp)" />
          </AreaChart>
        </ResponsiveContainer>
        <div className="flex gap-4 mt-1 text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#E53935] inline-block" /> Sign-ups</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#222222] inline-block" /> Completions</span>
        </div>
      </div>
    </div>
  );
};
